<script>
import DatePicker from '@/components/booking/bookingList/date-picker.vue'

export default {
  name: 'ExportDateRange',
  components: { DatePicker },
  props: {
    selectedRange: { type: String, default: 'custom' },
    customDateRange: { type: Array, default: () => [] },
    condition: { type: String, default: 'is' },
  },
  emits: ['update:selectedRange', 'update:customDateRange', 'update:condition'],
  data() {
    return {
      options: [
        { value: 'last7', label: 'Last 7 days' },
        { value: 'last30', label: 'Last 30 days' },
        { value: 'custom', label: 'Custom' },
      ],
      localDateRange: [...this.customDateRange],
      showDropdown: false,
      isDateRangePicker: false,
      dropdownCoords: { top: 0, left: 0 },
    }
  },
  watch: {
    customDateRange(newVal) {
      this.localDateRange = [...newVal]
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    handleClickOutside(event) {
      const dropdown = this.$refs.dropdown
      const popup = document.querySelector('.custom-datepicker-popup')
      const optionsList = document.querySelector('.custom-options')
      const vuetifyDropdown = event.target.closest('.v-menu__content')

      const clickedInside =
        (dropdown && dropdown.contains(event.target)) ||
        (popup && popup.contains(event.target)) ||
        (optionsList && optionsList.contains(event.target)) ||
        vuetifyDropdown

      if (!clickedInside) {
        this.showDropdown = false
      }
    },
    toggleDropdown() {
      this.showDropdown = !this.showDropdown
      this.isDateRangePicker = false
      this.$nextTick(() => {
        const el = this.$refs.dropdown
        if (el) {
          const rect = el.getBoundingClientRect()

          const popupWidth = 300
          const popupHeight = 400
          const windowWidth = window.innerWidth
          const windowHeight = window.innerHeight

          let left = rect.left + window.scrollX
          if (left + popupWidth > windowWidth) {
            left = windowWidth - popupWidth - 10
          }

          let top = rect.bottom + window.scrollY
          if (top + popupHeight > windowHeight + window.scrollY) {
            top = rect.top + window.scrollY - popupHeight - 10
          }

          this.dropdownCoords = { top, left }
        }
      })
    },
    selectOption(value) {
      if (this.selectedRange === value) {
        this.showDropdown = false
        if (this.selectedRange === 'custom' && !this.isDateRangePicker) {
          this.isDateRangePicker = true
        }
        return
      }
      this.$emit('update:selectedRange', value)

      if (value === 'custom') {
        this.$emit('update:customDateRange', [])
        this.showDropdown = false
        this.isDateRangePicker = true
      } else {
        this.$emit('update:customDateRange', [])
        this.isDateRangePicker = false
        this.showDropdown = false
      }
    },
    labelFor(value) {
      if (value === 'custom' && this.localDateRange.length === 2) {
        const [start, end] = this.localDateRange
        return `${this.formatDate(start)} - ${this.formatDate(end)}`
      }
      const opt = this.options.find((o) => o.value === value)
      return opt ? opt.label : value
    },
    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      return d.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    },
    clearFields() {
      this.localDateRange = []
      this.$emit('update:customDateRange', this.localDateRange)
    },
    closeDateFilter() {
      this.showDropdown = true
      this.isDateRangePicker = false
      this.localDateRange = []
      this.$emit('update:selectedRange', 'last30')
    },
    confirmDateFilter() {
      this.isDateRangePicker = false
      this.$emit('update:customDateRange', this.localDateRange)
    },
  },
}
</script>

<template>
  <div
    ref="dropdown"
    :class="[
      'custom-select-wrapper',
      { active: showDropdown || isDateRangePicker },
    ]"
  >
    <!-- Trigger -->
    <div class="custom-select-box" @click="toggleDropdown">
      <span>{{ labelFor(selectedRange) }}</span>
      <v-icon size="20">mdi-menu-down</v-icon>
    </div>

    <!-- Options -->
    <ul v-if="showDropdown" class="custom-options">
      <li
        v-for="option in options"
        :key="option.value"
        :class="{ selected: selectedRange === option.value }"
        @click.stop="selectOption(option.value)"
      >
        {{ option.label }}
        <v-icon
          v-if="selectedRange === option.value"
          class="check-icon"
          size="20"
        >
          mdi-check
        </v-icon>
      </li>
    </ul>

    <!-- DatePicker -->
    <DatePicker
      v-if="isDateRangePicker"
      v-model="localDateRange"
      :calendar-type="'range'"
      :condition="condition"
      @update-condition="$emit('update:condition', $event)"
      @clear-fields="clearFields"
      @close-date-filter="closeDateFilter"
      @confirm-date-filter="confirmDateFilter"
    />
  </div>
</template>

<style lang="scss">
@import '@/styles/_colors.scss';
@import '@/styles/bookingList/booking-export.scss';
</style>
